import Phaser from 'phaser'
import { t } from '../../i18n'
import { loadAssets } from '../JKGame_assets'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { COLORS, FONT } from '../JKGame_ui'

export class JKGame_PreloadScene extends Phaser.Scene {
  private loadFailed = false // set on any 'loaderror'; create() shows a localized notice instead of booting

  constructor() {
    super('Preload')
  }

  preload(): void {
    this.loadFailed = false // reset on (re)entry — Phaser reuses the scene instance
    this.cameras.main.setBackgroundColor(COLORS.bg)
    const cx = DESIGN_WIDTH / 2
    const cy = DESIGN_HEIGHT / 2
    const title = this.add.text(cx, cy - 60, t('app.title'), { fontFamily: FONT, fontSize: '40px', color: COLORS.text, fontStyle: 'bold' }).setOrigin(0.5)
    // Shrink to fit (consistency with every other scene title) so a wide localized
    // title never clips on a narrow viewport.
    const maxTitleW = DESIGN_WIDTH - 32
    if (title.width > maxTitleW) title.setScale(maxTitleW / title.width)
    this.add.text(cx, cy - 18, t('app.loading'), { fontFamily: FONT, fontSize: '15px', color: COLORS.sub }).setOrigin(0.5)

    const barW = 280
    this.add.rectangle(cx, cy + 20, barW, 16, COLORS.barBg).setOrigin(0.5)
    const fill = this.add.rectangle(cx - barW / 2, cy + 20, 1, 16, 0x5ee6a8).setOrigin(0, 0.5)
    // Live numeric readout below the bar — concrete progress on slow mobile links (the
    // bar alone reads as "stuck" when a large asset streams). Locale-neutral (a number),
    // so no new i18n key. Starts at 0%; the loader's 'progress' fires through to 100%.
    const pct = this.add.text(cx, cy + 44, '0%', { fontFamily: FONT, fontSize: '13px', color: COLORS.sub }).setOrigin(0.5)
    this.load.on('progress', (p: number) => {
      fill.setSize(Math.max(1, barW * p), 16)
      pct.setText(`${Math.round(p * 100)}%`)
    })
    // Never boot into a silently-broken game: if any asset fails (network drop / bad
    // deploy), flag it so create() shows a localized "refresh to retry" notice instead
    // of starting Boot with missing textures. Phaser still finishes the load phase, so
    // create() runs regardless — the flag is what gates the transition.
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      // Player sees the localized notice (create → showLoadError); this line is for QA/prod
      // diagnosis — naming the failed asset turns "the game won't load" into a fixable lead.
      console.warn(`[JKGame] asset load failed: ${file.key} (${file.src})`)
      this.loadFailed = true
    })

    loadAssets(this)
  }

  create(): void {
    if (this.loadFailed) {
      this.showLoadError()
      return // do NOT proceed to Boot — a missing-texture game is worse than a clear notice
    }
    // Player walk cycle (down-facing row); flipX handles left/right at the call site.
    if (!this.anims.exists('walk')) {
      this.anims.create({
        key: 'walk',
        frames: this.anims.generateFrameNumbers('char_walk', { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1,
      })
    }
    this.scene.start('Boot')
  }

  /** Replace the loading UI with a localized, legible failure notice (refresh to retry).
   *  No raw key, no silent boot into a broken game. */
  private showLoadError(): void {
    const cx = DESIGN_WIDTH / 2
    const cy = DESIGN_HEIGHT / 2
    // Cover the (now-misleading) progress bar/percent so only the error reads.
    this.add.rectangle(cx, cy + 20, DESIGN_WIDTH, 120, COLORS.bg).setOrigin(0.5)
    this.add.text(cx, cy + 20, t('app.loadError'), {
      fontFamily: FONT, fontSize: '17px', color: COLORS.bad, align: 'center',
      wordWrap: { width: DESIGN_WIDTH - 64, useAdvancedWrap: true },
      lineSpacing: 6,
    }).setOrigin(0.5)
  }
}
